import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import type { ILogicGraph } from '@/interfaces/models';
import type { Side } from '@/schemas/tiles';

export interface AuthoredCableEndpoint {
  linkId: string;
  side: Side;
}

export interface AuthoredCableChannel {
  /** Stable across graph node regeneration; derived from authored interface links. */
  key: string;
  endpoints: AuthoredCableEndpoint[];
}

/**
 * Recognize actual passive authored nets, rather than granting a chip-shaped
 * crossing shortcut. Active logic, including registers, is never replicated.
 * Each disconnected template component remains its own physical cable channel.
 */
export function authoredCableChannels(
  graph: ILogicGraph | undefined,
  interfaces: ReadonlyMap<string, Partial<Record<Side, NodeHandle>>>,
): AuthoredCableChannel[] {
  if (!graph?.nodes.size || [...graph.nodes.values()].some((n) => n.type !== 'WIRE'))
    return [];
  const parent = new Map([...graph.nodes.keys()].map((id) => [id, id]));
  const find = (id: NodeHandle): NodeHandle => {
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root)!;
    let cursor = id;
    while (cursor !== root) {
      const next = parent.get(cursor)!;
      parent.set(cursor, root);
      cursor = next;
    }
    return root;
  };
  for (const edge of graph.edges.values()) {
    if (!parent.has(edge.sourceNodeId) || !parent.has(edge.targetNodeId)) continue;
    parent.set(find(edge.targetNodeId), find(edge.sourceNodeId));
  }
  const components = new Map<NodeHandle, AuthoredCableEndpoint[]>();
  for (const [linkId, sides] of interfaces) {
    for (const [side, nodeId] of Object.entries(sides)) {
      if (!nodeId || !parent.has(nodeId)) continue;
      const root = find(nodeId);
      const endpoints = components.get(root) ?? [];
      endpoints.push({ linkId, side: side as Side });
      components.set(root, endpoints);
    }
  }
  return [...components.values()]
    .filter((endpoints) => new Set(endpoints.map((e) => e.linkId)).size > 1)
    .map((endpoints) => {
      endpoints.sort((a, b) =>
        `${a.linkId}:${a.side}`.localeCompare(`${b.linkId}:${b.side}`),
      );
      return { key: JSON.stringify(endpoints), endpoints };
    });
}

export function getAuthoredCableEndpoints(
  instances: readonly import('@/interfaces/models').IChipInstanceModel[],
  tiles: readonly import('@/models/tiles/TileInstance').TileInstance[],
) {
  const byOwner = new Map<
    string,
    Map<string, import('@/models/tiles/TileInstance').TileInstance>
  >();
  for (const tile of tiles) {
    const owner = tile.meta.partOfChip;
    const linkId = (tile.state as { connectedInterface?: { interfaceLinkId?: string } })
      ?.connectedInterface?.interfaceLinkId;
    if (!owner || !linkId) continue;
    const links = byOwner.get(owner) ?? new Map();
    links.set(linkId, tile);
    byOwner.set(owner, links);
  }
  const endpoints: Array<{
    tile: import('@/models/tiles/TileInstance').TileInstance;
    side: Side;
    channelKey: string;
    runtimeKey: string;
  }> = [];
  for (const instance of instances) {
    const chip = instance.chip;
    if (!chip || chip.kind === 'bundle-adapter' || instance.bundleWidth) continue;
    const links = byOwner.get(instance.id);
    if (!links) continue;
    for (const channel of authoredCableChannels(
      chip.templateGraph ?? undefined,
      chip.interfaceNodeMap,
    )) {
      const channelKey = JSON.stringify(['authored-cable', instance.id, channel.key]);
      for (const endpoint of channel.endpoints) {
        const tile = links.get(endpoint.linkId);
        if (tile)
          endpoints.push({
            tile,
            side: endpoint.side,
            channelKey,
            runtimeKey: channelKey,
          });
      }
    }
  }
  return endpoints;
}
